
import React, { useEffect, useState } from 'react';
import { Card, Button } from '../components/UI';
import { apiRequest } from '../utils/api';
import { FileJson, Save, Trash2, RefreshCw, Braces, AlertTriangle, CheckCircle, FileText, CheckSquare, Square, Lock, Info, Code } from 'lucide-react';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

interface ArchiveFile {
    id: number;
    name: string;
    created_at: string;
    linked_event_id: string | null;
    size_bytes?: number; 
}

export const FileEditor: React.FC = () => {
    const { user } = useAuth();
    const [files, setFiles] = useState<ArchiveFile[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [content, setContent] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [viewMode, setViewMode] = useState<'JSON' | 'TEXT'>('JSON');

    if (!user || !user.roles.includes(UserRole.WEBADMIN)) {
        return <Navigate to="/" />;
    }

    const loadList = async () => {
        setLoading(true);
        try {
            const res = await apiRequest<ArchiveFile[]>('telemetry.php');
            if (Array.isArray(res)) {
                setFiles(res);
            } else {
                setFiles([]);
            }
            setSelectedIds([]);
        } catch(e) {
            console.error("List load error", e);
        } finally {
            setLoading(false);
        }
    };

    const loadFile = async (id: number) => {
        setLoading(true);
        setSelectedId(id);
        setStatusMsg('');
        setContent('Lade Inhalt...'); 
        
        try {
            // FIX: Pfad-Logik an apiRequest anpassen. 
            // Wenn loadList('telemetry.php') geht, muss hier auch 'telemetry.php' (ohne /php/) genutzt werden.
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const url = `${baseUrl}/telemetry.php?id=${id}&mode=raw`;
            
            const res = await fetch(url);
            
            if (res.status === 404) throw new Error("Datei-ID nicht gefunden (404). Pfad oder ID falsch.");
            if (!res.ok) throw new Error(`HTTP Fehler: ${res.status}`);
            
            const text = await res.text();
            
            // Prüfen ob die Antwort eine JSON-Fehlermeldung ist
            if (text.trim().startsWith('{"status":"error"')) {
                try {
                    const errObj = JSON.parse(text);
                    throw new Error(errObj.message);
                } catch(e) { /* ignore parse error, treat as content */ }
            }

            // Versuchen, den Inhalt schön zu formatieren, wenn es JSON ist
            try {
                // Bei riesigen Dateien kann JSON.parse hier im Browser crashen (OOM).
                // Wir riskieren es, fangen den Fehler aber ab.
                const json = JSON.parse(text);
                
                // Falls der Server doch den JSON-Wrapper geschickt hat (sollte im Raw-Mode nicht passieren, aber zur Sicherheit)
                if (json.status === 'success' && json.data) {
                     const realData = json.data;
                     if (typeof realData === 'object') {
                         setContent(JSON.stringify(realData, null, 2));
                     } else {
                         setContent(String(realData));
                     }
                } else {
                    setContent(JSON.stringify(json, null, 2));
                }
                setViewMode('JSON');
            } catch {
                // Kein valides JSON oder zu groß -> Raw Text anzeigen
                setContent(text);
                setViewMode('TEXT');
                setStatusMsg("Inhalt ist kein valides JSON oder zu groß für den Formatter. Zeige Rohdaten.");
            }

        } catch(e: any) {
            setContent(`Fehler beim Laden: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadList();
    }, []);

    const handleFormat = () => {
        try {
            const obj = JSON.parse(content);
            const pretty = JSON.stringify(obj, null, 4);
            setContent(pretty);
            setStatusMsg('Formatiert.');
            setViewMode('JSON');
        } catch (e) {
            alert("Kann nicht formatieren: Der Inhalt ist kein gültiges JSON.");
        }
    };

    const handleSave = async () => {
        if (!selectedId) return;
        
        try {
            JSON.parse(content);
        } catch (e) {
            if (!confirm("WARNUNG: Der Inhalt ist kein valides JSON. Das kann zu Fehlern in der Auswertung führen. Trotzdem speichern?")) return;
        }

        setSaving(true);
        try {
            await apiRequest('telemetry.php', 'PUT', {
                id: selectedId,
                content: content
            });
            setStatusMsg('Gespeichert!');
            setTimeout(() => setStatusMsg(''), 3000);
            loadList(); 
        } catch (e: any) {
            alert("Fehler beim Speichern: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedId || !confirm("Diese Datei unwiderruflich löschen?")) return;
        try {
            await apiRequest(`telemetry.php?id=${selectedId}`, 'DELETE');
            setContent('');
            setSelectedId(null);
            loadList();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const toggleSelect = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (selectedIds.includes(id)) {
            setSelectedIds(prev => prev.filter(x => x !== id));
        } else {
            setSelectedIds(prev => [...prev, id]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!confirm(`Wirklich ${selectedIds.length} Dateien löschen?`)) return;

        setLoading(true);
        try {
            for (const id of selectedIds) {
                await apiRequest(`telemetry.php?id=${id}`, 'DELETE');
            }
            setContent('');
            setSelectedId(null);
            await loadList();
            alert("Dateien gelöscht.");
        } catch (e: any) {
            alert("Fehler beim Löschen: " + e.message);
            loadList();
        } finally {
            setLoading(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === files.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(files.map(f => f.id));
        }
    };

    const formatSize = (bytes?: number) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(2) + ' MB';
    };

    const selectedFile = files.find(f => f.id === selectedId);
    const isLocked = selectedFile && !!selectedFile.linked_event_id;

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-2">
                    <FileJson className="text-f1-red"/> Systemdateien bearbeiten
                    </h1>
                    <p className="text-gray-400 text-sm">Raw Database Inspector (Webadmin Only)</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" icon={RefreshCw} onClick={loadList}>Liste aktualisieren</Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)] min-h-[500px]">
                {/* SIDEBAR LIST */}
                <div className="bg-f1-carbon border border-gray-800 rounded-lg flex flex-col overflow-hidden">
                    <div className="p-3 bg-gray-900 border-b border-gray-800 font-bold text-xs uppercase text-gray-500 flex justify-between items-center">
                        <div className="flex items-center gap-2 cursor-pointer hover:text-white" onClick={toggleSelectAll}>
                            {selectedIds.length > 0 && selectedIds.length === files.length ? <CheckSquare size={14} className="text-f1-red"/> : <Square size={14}/>}
                            Telemetry Archives ({files.length})
                        </div>
                        {selectedIds.length > 0 && (
                            <button onClick={handleBulkDelete} className="text-red-500 hover:text-red-400 text-[10px] font-bold uppercase flex items-center gap-1 bg-red-900/20 px-2 py-1 rounded border border-red-900/50">
                                <Trash2 size={10}/> Löschen ({selectedIds.length})
                            </button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {loading && !selectedId && <div className="text-center p-4"><RefreshCw className="animate-spin mx-auto"/></div>}
                        {files.map(f => {
                            const isChecked = selectedIds.includes(f.id);
                            const isEmpty = !f.size_bytes || f.size_bytes < 10;
                            return (
                                <div 
                                    key={f.id}
                                    onClick={() => loadFile(f.id)}
                                    className={`p-3 rounded cursor-pointer border transition-colors relative group ${selectedId === f.id ? 'bg-blue-900/20 border-blue-600 text-white' : 'bg-transparent border-transparent hover:bg-gray-800 text-gray-400'}`}
                                >
                                    <div 
                                        className="absolute left-2 top-3 z-10 text-gray-600 hover:text-f1-red"
                                        onClick={(e) => toggleSelect(f.id, e)}
                                    >
                                        {isChecked ? <CheckSquare size={16} className="text-f1-red"/> : <Square size={16}/>}
                                    </div>
                                    <div className="pl-6">
                                        <div className="flex items-center gap-2">
                                            {f.linked_event_id ? <Lock size={14} className="text-yellow-500"/> : <FileText size={16}/>}
                                            <div className={`font-bold text-sm truncate w-full ${f.linked_event_id ? 'text-yellow-500' : ''}`}>{f.name}</div>
                                        </div>
                                        <div className="text-[10px] text-gray-600 flex justify-between mt-1">
                                            <span>{new Date(f.created_at).toLocaleDateString()}</span>
                                            <span className={isEmpty ? 'text-red-500 font-bold' : ''}>{formatSize(f.size_bytes)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* EDITOR AREA */}
                <div className="lg:col-span-3 flex flex-col bg-[#1e1e1e] border border-gray-700 rounded-lg overflow-hidden shadow-2xl">
                    {/* TOOLBAR */}
                    <div className="p-2 bg-gray-900 border-b border-gray-700 flex justify-between items-center">
                        <div className="flex gap-2 items-center">
                            <Button size="sm" variant="secondary" onClick={handleFormat} disabled={!content}>
                                <Braces size={14} className="mr-2"/> Format
                            </Button>
                            
                            {viewMode === 'JSON' ? 
                                <span className="text-xs font-mono text-green-500 bg-green-900/20 px-2 py-1 rounded">JSON Mode</span> :
                                <span className="text-xs font-mono text-gray-500 bg-gray-800 px-2 py-1 rounded">Text Mode</span>
                            }

                            {statusMsg && <span className="text-yellow-500 text-xs flex items-center gap-1 animate-fade-in"><AlertTriangle size={12}/> {statusMsg}</span>}
                            {isLocked && <span className="text-yellow-500 text-xs flex items-center gap-1 ml-2 bg-yellow-900/20 px-2 py-1 rounded border border-yellow-700"><Lock size={10}/> Used in Results</span>}
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="danger" onClick={handleDelete} disabled={!selectedId || isLocked}>
                                <Trash2 size={14} className="mr-2"/> Löschen
                            </Button>
                            <Button size="sm" onClick={handleSave} disabled={!selectedId || saving}>
                                {saving ? 'Speichern...' : <><Save size={14} className="mr-2"/> Speichern</>}
                            </Button>
                        </div>
                    </div>

                    {/* TEXTAREA */}
                    <div className="flex-1 relative">
                        {loading && selectedId ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                                <RefreshCw className="animate-spin text-white w-8 h-8"/>
                            </div>
                        ) : null}
                        
                        {!selectedId ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-600">
                                <FileJson size={64} className="mb-4 opacity-20"/>
                                <p>Wähle eine Datei zum Bearbeiten</p>
                            </div>
                        ) : (
                            <textarea 
                                className={`w-full h-full bg-[#1e1e1e] font-mono text-xs p-4 outline-none resize-none ${viewMode === 'JSON' ? 'text-green-400' : 'text-gray-300'}`}
                                value={content}
                                onChange={e => setContent(e.target.value)}
                                spellCheck={false}
                            />
                        )}
                    </div>
                    
                    {/* FOOTER INFO */}
                    <div className="px-4 py-1 bg-blue-900/20 border-t border-blue-900/50 text-[10px] text-blue-300 flex items-center gap-2">
                        <AlertTriangle size={10}/>
                        <span>RAW EDIT MODE: Bearbeite JSON-Strukturen nur wenn du weißt, was du tust. Ungültiges JSON kann Importe zerstören.</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
